import { TaskBlock } from 'shared/express-interface/blocks-core/TaskBlock'
import { RcFile } from 'antd/lib/upload'
import Flex from 'antd/lib/flex'
import Tooltip from 'antd/lib/tooltip'
import CloseOutlined from '@ant-design/icons/lib/icons/CloseOutlined'
import Upload from 'antd/lib/upload'
import Button from 'antd/lib/button'
import UploadOutlined from '@ant-design/icons/lib/icons/UploadOutlined'
import { ActionButton } from 'Components/common/ActionButton/ActionButton'
import SubmitTurnIcon from 'Assets/icons/arrow-right.svg'
import { getFileDownloadUrl, uploadFile } from 'Services/tasks.service'
import { useCallback } from 'react'
import { downloadPublicFile } from 'Utils/common.utils'
import { useMessage } from 'shared/hooks/use-message'
import { FileTextFilled } from '@ant-design/icons'
import { FileToModelValueType } from 'Types/Attribute.types'

type EditorContainerProps = {
    inputEditorBlockElement: TaskBlock<Record<string, unknown>>
    filesList: FileToModelValueType[]
    setFilesList: (filesList: FileToModelValueType[]) => void
    buttonDisabledReason: string
    submitTurn: (editIndex: number) => void
    setEditIndex: (editIndex: number) => void
    editIndex: number
    taskId: string
    isVisible: boolean
    setIsFileUploading: (isFileUploading: boolean) => void
    isReadOnly: boolean
}

export const EditorContainer = (props: EditorContainerProps) => {
    const {
        inputEditorBlockElement,
        filesList,
        setFilesList,
        buttonDisabledReason,
        submitTurn,
        setEditIndex,
        editIndex,
        taskId,
        isVisible,
        setIsFileUploading,
        isReadOnly,
    } = props

    const [messageApi] = useMessage()

    const onSubmitEditContent = useCallback(() => {
        submitTurn(editIndex)
        setEditIndex(-1)
    }, [editIndex, submitTurn, setEditIndex])

    const downloadFile = useCallback(
        async (path: string) => {
            // We don't need to download files if we are not in edit mode
            if (editIndex === -1) {
                return
            }
            const downloadURL = await getFileDownloadUrl(path)

            if (downloadURL) {
                downloadPublicFile(downloadURL)
            }
        },
        [editIndex]
    )

    const handleUpload = async (file: RcFile) => {
        setIsFileUploading(true)

        try {
            const res = await uploadFile(
                file,
                `expertsvc/model_conv_v2/${Date.now()}/${file.name}`,
                taskId
            )
            // TODO - Handle getting file id based on the model which we're going to use
            setFilesList([...filesList, { ...res, file_id: '' }])
            messageApi.success('File uploaded successfully')
        } catch (err) {
            messageApi.error('File upload failed')
            console.error(err)
        } finally {
            setIsFileUploading(false)
        }
    }

    const getFileName = (filePath: string) => {
        return filePath.split('/').pop() ?? ''
    }

    return (
        <div className={`input-container ${isVisible ? '' : 'hidden'}`}>
            {inputEditorBlockElement.getElement()?.element}
            <Flex
                vertical
                className="submit-turns-actions-container"
                align="flex-end"
                gap={'10px 0'}
            >
                <Flex
                    className="files-list-container"
                    gap={'0 10px'}
                    wrap="nowrap"
                    justify="flex-end"
                >
                    {filesList?.map((file: FileToModelValueType, id: number) => (
                        <div
                            key={id}
                            className="file-item"
                            onClick={() => downloadFile(file.path)}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    downloadFile(file.path)
                                }
                            }}
                            tabIndex={0}
                            role="button"
                            aria-label={`Download file ${getFileName(file.path)}`}
                        >
                            <FileTextFilled className="file-icon" />
                            {getFileName(file.path)}
                            <CloseOutlined
                                onClick={e => {
                                    setFilesList(
                                        filesList.filter(
                                            (f: FileToModelValueType) => f.path !== file.path
                                        )
                                    )
                                    e.stopPropagation()
                                }}
                            />
                        </div>
                    ))}
                </Flex>
                <Flex align="center" gap={0}>
                    {editIndex !== -1 && (
                        <Button className="close-button" onClick={() => setEditIndex(-1)}>
                            Cancel
                        </Button>
                    )}
                    <Upload
                        beforeUpload={file => {
                            handleUpload(file)
                            return false
                        }}
                        showUploadList={false}
                        maxCount={1}
                        disabled={isReadOnly}
                    >
                        <Button
                            icon={<UploadOutlined />}
                            className="upload-file-button"
                            disabled={isReadOnly}
                        >
                            Attach files
                        </Button>
                    </Upload>
                    <Tooltip
                        title={buttonDisabledReason}
                        data-testid="copy-button-wrapper"
                        placement="top"
                        trigger={'hover'}
                    >
                        <ActionButton
                            title={<img src={SubmitTurnIcon} alt="submit" />}
                            className="submit-turn-button"
                            onClick={onSubmitEditContent}
                            disabled={!!buttonDisabledReason}
                        />
                    </Tooltip>
                </Flex>
            </Flex>
        </div>
    )
}
